import React, { useState } from "react";
import {
  Eye,
  EyeOff,
  User,
  Mail,
  Phone,
  Key,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Building2,
  Shield,
  Sparkles,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import LoginPage from "../auth/Login";

// Import the modern LoginPage component

const EmployeeAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authMode, setAuthMode] = useState("login"); // 'login', 'signup', 'verify-eid', 'verify-email', 'set-password'
  const [formData, setFormData] = useState({
    eid: "",
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
    otp: "",
    loginIdentifier: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [employeeData, setEmployeeData] = useState(null);
  const [focusedField, setFocusedField] = useState(null);

  // Mock registered users (in real app, this would be in database)
  const registeredUsers = {
    "john.doe@company.com": {
      eid: "EMP001",
      password: "password123",
      mobile: "+1-555-0123",
    },
    "+1-555-0123": {
      eid: "EMP001",
      password: "password123",
      email: "john.doe@company.com",
    },
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
    setSuccess("");
  };

  const validateEID = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/auth/validate-eid`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ eid: formData.eid }),
        }
      );

      const data = await response.json().catch(() => null); // safe parse

      if (!response.ok) {
        // ✅ Use backend-provided message if available
        const errorMessage = data?.detail || "Failed to validate Employee ID";
        throw new Error(errorMessage);
      }

      console.log("EID Validation Response:", data);

      setEmployeeData({
        name: data.name,
        eid: data.eid,
        email: data.email,
        phone: data.phone,
      });

      setFormData((prev) => ({
        ...prev,
        email: data.email,
        mobile: data.phone,
      }));

      setSuccess(
        `Welcome ${data.name}! Your contact details have been pre-filled. Please verify and continue.`
      );
      setAuthMode("verify-email");
    } catch (err) {
      console.log("EID validation error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendOTP = async (eid) => {
    setLoading(true);
    setError("");

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    // Validate mobile format
    const mobileRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (!mobileRegex.test(formData.mobile.replace(/[-\s]/g, ""))) {
      setError("Please enter a valid mobile number.");
      setLoading(false);
      return;
    }

    // Simulate sending OTP
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/auth/send-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify({ eid }),
        }
      );

      const data = await response.json().catch(() => null); // safe parse

      if (!response.ok) {
        // ✅ Use backend-provided message if available
        const errorMessage = data?.detail || "Failed to send OTP";
        throw new Error(errorMessage);
      }

      console.log("OTP sent successfully!");
      setOtpSent(true);
      setSuccess("OTP sent to your email address. Please check your inbox.");
      setLoading(false);
    } catch (err) {
      console.log("OTP sending error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    setLoading(true);
    setError("");

    try {
      // Using dummy OTP 123456 as requested
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/email/validate-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: formData.email ? formData.email : "null",
            eid: formData.eid,
            otp: formData.otp,
          }),
        }
      );
      const data = await response.json().catch(() => null); // safe parse
      console.log("OTP Validation Response:", data);
      if (data.status === "success") {
        setSuccess("Email verified successfully! Please set your password.");
        setAuthMode("set-password");
      } else {
        setError("Invalid Verification Code. Please try again.");
      }
      setLoading(false);
    } catch (error) {
      console.log("OTP verification error:", error);
      setError(error.message);
      setLoading(false);
    }
  };

  const setPassword = async () => {
    setLoading(true);
    setError("");

    // Validate password
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters long.");
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/users/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            eid: formData.eid,
            email: formData.email,
            username: employeeData?.name,
            right: "user",
            is_active: false,
            is_superuser: false,
            password: formData.password,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create account");
      }

      const data = await response.json();
      console.log("User creation response:", data);

      setSuccess(
        "Account created successfully! You can now login with your credentials."
      );

      // Reset form and go back to login
      setTimeout(() => {
        setAuthMode("login");
        setFormData({
          eid: "",
          email: "",
          mobile: "",
          password: "",
          confirmPassword: "",
          otp: "",
          loginIdentifier: "",
        });
        setEmployeeData(null);
        setOtpSent(false);
        setLoading(false);
      }, 2000);
    } catch (err) {
      console.error("Account creation error:", err);
      setError(
        "Failed to create account. Please try again or contact support."
      );
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      eid: "",
      email: "",
      mobile: "",
      password: "",
      confirmPassword: "",
      otp: "",
      loginIdentifier: "",
    });
    setError("");
    setSuccess("");
    setOtpSent(false);
    setEmployeeData(null);
  };

  const renderEIDVerification = () => (
    <div className="space-y-6">
      <div className="text-center">
        <button
          onClick={() => {
            setAuthMode("login");
            resetForm();
          }}
          className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-6 transition-colors duration-200"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Login
        </button>

        <div className="mb-6">
          <div className="bg-blue-500/20 backdrop-blur-sm p-4 rounded-full w-16 h-16 mx-auto mb-4 border border-blue-400/30">
            <Shield className="h-8 w-8 text-blue-400 mx-auto mt-1" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Verify Employee/Ref ID
          </h2>
          <p className="text-gray-300">
            Enter your Employee/Ref ID to begin account setup
          </p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-200 mb-2">
          Employee/Ref ID
        </label>
        <div className="relative">
          <User
            className={`absolute left-3 top-3 h-5 w-5 transition-colors duration-200 ${
              focusedField === "eid" ? "text-blue-400" : "text-gray-400"
            }`}
          />
          <input
            type="text"
            name="eid"
            value={formData.eid}
            onChange={handleInputChange}
            onFocus={() => setFocusedField("eid")}
            onBlur={() => setFocusedField(null)}
            className={`w-full pl-12 pr-4 py-3 bg-white/10 border rounded-2xl backdrop-blur-sm placeholder-gray-400 text-white focus:outline-none transition-all duration-300 ${
              focusedField === "eid"
                ? "border-blue-400/50 bg-blue-500/10 shadow-lg shadow-blue-500/20 transform scale-[1.02]"
                : "border-white/20 hover:border-white/30"
            }`}
            placeholder="Enter your Employee/Ref ID"
          />
        </div>
      </div>

      <button
        onClick={validateEID}
        disabled={loading || !formData.eid}
        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 px-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-semibold transform hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/25"
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
            Verifying...
          </div>
        ) : (
          "Verify Employee/Ref ID"
        )}
      </button>
    </div>
  );

  const renderEmailVerification = () => (
    <div className="space-y-6">
      <div className="text-center">
        <button
          onClick={() => {
            setAuthMode("verify-eid");
            resetForm();
          }}
          className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-6 transition-colors duration-200"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </button>

        <div className="mb-6">
          <div className="bg-green-500/20 backdrop-blur-sm p-4 rounded-full w-16 h-16 mx-auto mb-4 border border-green-400/30">
            <Mail className="h-8 w-8 text-green-400 mx-auto mt-1" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Contact Verification
          </h2>
          <p className="text-gray-300">Verify your contact information</p>
        </div>

        {employeeData && (
          <div className="bg-green-500/20 border border-green-400/30 rounded-2xl p-4 mb-6 backdrop-blur-sm">
            <div className="flex items-center justify-center mb-2">
              <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
              <span className="font-semibold text-green-300">
                Employee Verified
              </span>
            </div>
            <p className="text-green-200 font-medium">{employeeData.name}</p>
            <p className="text-green-300 text-sm">ID: {employeeData.eid}</p>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-200 mb-2">
            Email Address
          </label>
          <div className="relative">
            <Mail
              className={`absolute left-3 top-3 h-5 w-5 transition-colors duration-200 ${
                focusedField === "email" ? "text-blue-400" : "text-gray-400"
              }`}
            />
            <input
              type="email"
              name="email"
              disabled
              value={formData.email}
              onChange={handleInputChange}
              onFocus={() => setFocusedField("email")}
              onBlur={() => setFocusedField(null)}
              className={`w-full pl-12 pr-4 py-3 bg-white/10 border rounded-2xl backdrop-blur-sm placeholder-gray-400 text-white focus:outline-none transition-all duration-300 ${
                focusedField === "email"
                  ? "border-blue-400/50 bg-blue-500/10 shadow-lg shadow-blue-500/20 transform scale-[1.02]"
                  : "border-white/20 hover:border-white/30"
              }`}
              placeholder="Enter your email address"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-200 mb-2">
            Mobile Number
          </label>
          <div className="relative">
            <Phone
              className={`absolute left-3 top-3 h-5 w-5 transition-colors duration-200 ${
                focusedField === "mobile" ? "text-blue-400" : "text-gray-400"
              }`}
            />
            <input
              type="tel"
              name="mobile"
              value={formData.mobile}
              onChange={handleInputChange}
              onFocus={() => setFocusedField("mobile")}
              onBlur={() => setFocusedField(null)}
              className={`w-full pl-12 pr-4 py-3 bg-white/10 border rounded-2xl backdrop-blur-sm placeholder-gray-400 text-white focus:outline-none transition-all duration-300 ${
                focusedField === "mobile"
                  ? "border-blue-400/50 bg-blue-500/10 shadow-lg shadow-blue-500/20 transform scale-[1.02]"
                  : "border-white/20 hover:border-white/30"
              }`}
              placeholder="Enter your mobile number"
            />
          </div>
        </div>
      </div>

      {!otpSent ? (
        <button
          onClick={() => sendOTP(employeeData.eid)}
          disabled={loading || !formData.email || !formData.mobile}
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 px-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-semibold transform hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/25"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Sending OTP...
            </div>
          ) : (
            "Send Verification Code"
          )}
        </button>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-200 mb-2">
              Verification Code
            </label>
            <input
              type="text"
              name="otp"
              value={formData.otp}
              onChange={handleInputChange}
              onFocus={() => setFocusedField("otp")}
              onBlur={() => setFocusedField(null)}
              className={`w-full px-4 py-3 bg-white/10 border rounded-2xl backdrop-blur-sm placeholder-gray-400 text-white focus:outline-none transition-all duration-300 text-center text-lg font-mono tracking-widest ${
                focusedField === "otp"
                  ? "border-blue-400/50 bg-blue-500/10 shadow-lg shadow-blue-500/20 transform scale-[1.02]"
                  : "border-white/20 hover:border-white/30"
              }`}
              placeholder="Enter 6-digit code"
              maxLength="6"
            />
          </div>

          <button
            onClick={verifyOTP}
            disabled={loading || formData.otp.length !== 6}
            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-3 px-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-semibold transform hover:scale-[1.02] hover:shadow-xl hover:shadow-green-500/25"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Verifying...
              </div>
            ) : (
              "Verify Code"
            )}
          </button>
        </div>
      )}
    </div>
  );

  const renderSetPassword = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="bg-blue-500/20 backdrop-blur-sm p-4 rounded-full w-16 h-16 mx-auto mb-4 border border-blue-400/30">
          <Key className="h-8 w-8 text-blue-400 mx-auto mt-1" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Set Password</h2>
        <p className="text-gray-300">
          Create a secure password for your account
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-200 mb-2">
            Password
          </label>
          <div className="relative">
            <Key
              className={`absolute left-3 top-3 h-5 w-5 transition-colors duration-200 ${
                focusedField === "password" ? "text-blue-400" : "text-gray-400"
              }`}
            />
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              onFocus={() => setFocusedField("password")}
              onBlur={() => setFocusedField(null)}
              className={`w-full pl-12 pr-12 py-3 bg-white/10 border rounded-2xl backdrop-blur-sm placeholder-gray-400 text-white focus:outline-none transition-all duration-300 ${
                focusedField === "password"
                  ? "border-blue-400/50 bg-blue-500/10 shadow-lg shadow-blue-500/20 transform scale-[1.02]"
                  : "border-white/20 hover:border-white/30"
              }`}
              placeholder="Enter password (min 8 characters)"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-3 text-gray-400 hover:text-white transition-colors duration-200"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-200 mb-2">
            Confirm Password
          </label>
          <div className="relative">
            <Key
              className={`absolute left-3 top-3 h-5 w-5 transition-colors duration-200 ${
                focusedField === "confirmPassword"
                  ? "text-blue-400"
                  : "text-gray-400"
              }`}
            />
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              onFocus={() => setFocusedField("confirmPassword")}
              onBlur={() => setFocusedField(null)}
              className={`w-full pl-12 pr-12 py-3 bg-white/10 border rounded-2xl backdrop-blur-sm placeholder-gray-400 text-white focus:outline-none transition-all duration-300 ${
                focusedField === "confirmPassword"
                  ? "border-blue-400/50 bg-blue-500/10 shadow-lg shadow-blue-500/20 transform scale-[1.02]"
                  : "border-white/20 hover:border-white/30"
              }`}
              placeholder="Confirm your password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-3 text-gray-400 hover:text-white transition-colors duration-200"
            >
              {showConfirmPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-blue-500/20 border border-blue-400/30 rounded-2xl p-4 backdrop-blur-sm">
        <p className="text-sm font-medium text-blue-300 mb-2">
          Password Requirements:
        </p>
        <div className="grid grid-cols-1 gap-1 text-xs text-blue-200">
          <div className="flex items-center">
            <div
              className={`w-2 h-2 rounded-full mr-2 ${
                formData.password.length >= 8 ? "bg-green-400" : "bg-gray-500"
              }`}
            ></div>
            At least 8 characters long
          </div>
          {/* <div className="flex items-center">
            <div
              className={`w-2 h-2 rounded-full mr-2 ${
                /[A-Z]/.test(formData.password) &&
                /[a-z]/.test(formData.password)
                  ? "bg-green-400"
                  : "bg-gray-500"
              }`}
            ></div>
            Include uppercase and lowercase letters
          </div>
          <div className="flex items-center">
            <div
              className={`w-2 h-2 rounded-full mr-2 ${
                /\d/.test(formData.password) &&
                /[!@#$%^&*(),.?":{}|<>]/.test(formData.password)
                  ? "bg-green-400"
                  : "bg-gray-500"
              }`}
            ></div>
            Include numbers and special characters
          </div> */}
        </div>
      </div>

      <button
        onClick={setPassword}
        disabled={loading || !formData.password || !formData.confirmPassword}
        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-3 px-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-semibold transform hover:scale-[1.02] hover:shadow-xl hover:shadow-green-500/25"
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
            Creating Account...
          </div>
        ) : (
          "Create Account"
        )}
      </button>
    </div>
  );

  return (
    <>
      {authMode !== "login" ? (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
          {/* Animated background elements */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
            <div className="absolute top-40 right-20 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
          </div>

          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>

          <div className="max-w-md w-full relative z-10">
            {/* Company Logo */}
            <div className="flex items-center justify-center mb-8">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-3 rounded-xl shadow-lg backdrop-blur-xl">
                  <Building2 className="h-8 w-8 text-white" />
                </div>
                <div>
                  <div className="flex items-center justify-center space-x-2 mb-1">
                    <div className="h-6 w-32 rounded backdrop-blur-sm flex items-center justify-center">
                      <img
                        src="images/kajaria-logo.png"
                        alt="Logo 1"
                        className="filter h-6 md:h-6 w-32 sm:h-6"
                      />
                    </div>
                  </div>
                  <p className="text-sm font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                    Employee Portal
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/20">
              {/* Error/Success Messages */}
              {error && (
                <div className="mb-6 bg-red-500/20 border border-red-400/30 rounded-2xl p-4 backdrop-blur-sm animate-shake">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
                    <p className="text-red-300 text-sm font-medium">{error}</p>
                  </div>
                </div>
              )}

              {success && (
                <div className="mb-6 bg-green-500/20 border border-green-400/30 rounded-2xl p-4 backdrop-blur-sm animate-fadeIn">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-3" />
                    <p className="text-green-300 text-sm font-medium">
                      {success}
                    </p>
                  </div>
                </div>
              )}

              {/* Render different forms based on auth mode */}
              {authMode === "verify-eid" && renderEIDVerification()}
              {authMode === "verify-email" && renderEmailVerification()}
              {authMode === "set-password" && renderSetPassword()}
            </div>
          </div>

          <style jsx>{`
            .bg-grid-pattern {
              background-image: linear-gradient(
                  rgba(255, 255, 255, 0.1) 1px,
                  transparent 1px
                ),
                linear-gradient(
                  90deg,
                  rgba(255, 255, 255, 0.1) 1px,
                  transparent 1px
                );
              background-size: 20px 20px;
            }

            .animation-delay-2000 {
              animation-delay: 2s;
            }

            .animation-delay-4000 {
              animation-delay: 4s;
            }

            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translateY(-10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            @keyframes shake {
              0%,
              100% {
                transform: translateX(0);
              }
              25% {
                transform: translateX(-5px);
              }
              75% {
                transform: translateX(5px);
              }
            }

            @keyframes slideDown {
              from {
                opacity: 0;
                transform: translateY(-10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            .animate-fadeIn {
              animation: fadeIn 0.5s ease-out;
            }

            .animate-shake {
              animation: shake 0.5s ease-in-out;
            }

            .animate-slideDown {
              animation: slideDown 0.3s ease-out;
            }
          `}</style>
        </div>
      ) : (
        <LoginPage setAuthMode={setAuthMode} resetForm={resetForm} />
      )}
    </>
  );
};

export default EmployeeAuth;
